"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BeefyAPI = exports.BEEFY_VAULT_ERC20_DECIMALS = void 0;
const shared_models_1 = require("@railgun-community/shared-models");
const beefy_fetch_1 = require("./beefy-fetch");
const utils_1 = require("../../utils");
const cookbook_debug_1 = require("../../utils/cookbook-debug");
const basis_points_1 = require("../../utils/basis-points");
exports.BEEFY_VAULT_ERC20_DECIMALS = 18n;
class BeefyAPI {
    static cachedVaultData;
    static cacheTimestamp;
    static getBeefyChainInfoForNetwork(networkName) {
        switch (networkName) {
            case shared_models_1.NetworkName.Ethereum:
                return { network: 'ethereum', chain: 'ethereum' };
            case shared_models_1.NetworkName.BNBChain:
                return { network: 'bsc', chain: 'bsc' };
            case shared_models_1.NetworkName.Polygon:
                return { network: 'polygon', chain: 'polygon' };
            case shared_models_1.NetworkName.Arbitrum:
                return { network: 'arbitrum', chain: 'arbitrum' };
            case shared_models_1.NetworkName.EthereumRopsten_DEPRECATED:
            case shared_models_1.NetworkName.EthereumGoerli_DEPRECATED:
            case shared_models_1.NetworkName.PolygonMumbai_DEPRECATED:
            case shared_models_1.NetworkName.ArbitrumGoerli_DEPRECATED:
            case shared_models_1.NetworkName.EthereumSepolia:
            case shared_models_1.NetworkName.PolygonAmoy:
            case shared_models_1.NetworkName.Hardhat:
                throw new Error('Chain not supported by Beefy Vaults');
        }
    }
    static supportsNetwork(networkName) {
        try {
            this.getBeefyChainInfoForNetwork(networkName);
            return true;
        }
        catch {
            return false;
        }
    }
    static cacheExpired() {
        return (!(0, shared_models_1.isDefined)(this.cacheTimestamp) ||
            this.cacheTimestamp < Date.now() - 3 * 60 * 1000);
    }
    static async getBeefyVaultDataAllChains(skipCache, includeInactiveVaults = false) {
        if (!skipCache && this.cachedVaultData && !this.cacheExpired()) {
            return this.cachedVaultData;
        }
        const [beefyVaultAPIData, beefyFeesAPIData, beefyAPYAPIData,] = await Promise.all([
            (0, beefy_fetch_1.getBeefyAPIData)(beefy_fetch_1.BeefyApiEndpoint.GetVaults),
            (0, beefy_fetch_1.getBeefyAPIData)(beefy_fetch_1.BeefyApiEndpoint.GetFees),
            this.getBeefyVaultAPYs(),
        ]);
        const vaultData = (0, shared_models_1.removeUndefineds)(beefyVaultAPIData.map(vaultAPIData => {
            const feesData = beefyFeesAPIData[vaultAPIData.id];
            const apy = beefyAPYAPIData[vaultAPIData.id];
            if (feesData == null || apy == null) {
                return undefined;
            }
            const isActive = vaultAPIData.status === 'active';
            if (!includeInactiveVaults && !isActive) {
                return undefined;
            }
            if (!vaultAPIData.tokenAddress ||
                !vaultAPIData.earnedTokenAddress ||
                !vaultAPIData.earnContractAddress) {
                return undefined;
            }
            return this.convertAPIDataToBeefyVaultData(vaultAPIData, feesData, apy, isActive);
        }));
        this.cachedVaultData = vaultData;
        this.cacheTimestamp = Date.now();
        return vaultData;
    }
    static async getBeefyVaultAPY(vaultID) {
        const apys = await this.getBeefyVaultAPYs();
        return apys[vaultID];
    }
    static convertAPIDataToBeefyVaultData(vaultAPIData, feesData, apy, isActive) {
        try {
            const vaultInfo = {
                vaultID: vaultAPIData.id,
                vaultName: vaultAPIData.name,
                apy,
                chain: vaultAPIData.chain,
                network: vaultAPIData.network,
                depositERC20Symbol: vaultAPIData.token,
                depositERC20Address: vaultAPIData.tokenAddress.toLowerCase(),
                depositERC20Decimals: BigInt(vaultAPIData.tokenDecimals),
                vaultERC20Symbol: vaultAPIData.earnedToken,
                vaultERC20Address: vaultAPIData.earnedTokenAddress.toLowerCase(),
                vaultContractAddress: vaultAPIData.earnContractAddress.toLowerCase(),
                vaultRate: this.parseVaultRate(vaultAPIData.pricePerFullShare),
                depositFeeBasisPoints: (0, basis_points_1.numToBasisPoints)(feesData?.deposit),
                withdrawFeeBasisPoints: (0, basis_points_1.numToBasisPoints)(feesData?.withdraw),
                isActive,
            };
            return vaultInfo;
        }
        catch (cause) {
            if (!(cause instanceof Error)) {
                throw new Error('Unexpected non-error thrown', { cause });
            }
            return undefined;
        }
    }
    static parseVaultRate(pricePerFullShare) {
        return BigInt(pricePerFullShare);
    }
    static async getBeefyVaultAPYs() {
        const beefyAPYAPIData = await (0, beefy_fetch_1.getBeefyAPIData)(beefy_fetch_1.BeefyApiEndpoint.GetAPYs);
        return beefyAPYAPIData;
    }
    static async getBeefyVaultTVLs() {
        const beefyTVLAPIData = await (0, beefy_fetch_1.getBeefyAPIData)(beefy_fetch_1.BeefyApiEndpoint.GetTVLs);
        return beefyTVLAPIData;
    }
    static async getFilteredBeefyVaults(networkName, skipCache, includeInactiveVaults, depositERC20Address, vaultERC20Address) {
        try {
            const beefyVaultData = await this.getBeefyVaultDataAllChains(skipCache, includeInactiveVaults);
            const beefyChainInfo = this.getBeefyChainInfoForNetwork(networkName);
            let filtered = beefyVaultData.filter(vault => vault.chain === beefyChainInfo.chain &&
                vault.network === beefyChainInfo.network);
            if ((0, shared_models_1.isDefined)(depositERC20Address)) {
                filtered = filtered.filter(vault => (0, utils_1.compareTokenAddress)(vault.depositERC20Address, depositERC20Address));
            }
            if ((0, shared_models_1.isDefined)(vaultERC20Address)) {
                filtered = filtered.filter(vault => (0, utils_1.compareTokenAddress)(vault.vaultERC20Address, vaultERC20Address));
            }
            return filtered;
        }
        catch (cause) {
            if (!(cause instanceof Error)) {
                throw new Error('Unexpected non-error thrown', { cause });
            }
            cookbook_debug_1.CookbookDebug.error(cause);
            throw new Error('Could not get active list of Vaults.', { cause });
        }
    }
    static async getBeefyVaultForID(vaultID, networkName) {
        const beefyVaults = await this.getFilteredBeefyVaults(networkName, true, true);
        const beefyVault = beefyVaults.find(vault => vault.vaultID === vaultID);
        if (!beefyVault) {
            throw new Error(`Beefy vault with fees/apy data missing for ID: ${vaultID}.`);
        }
        if (!beefyVault.isActive) {
            throw new Error(`Beefy vault is not active for ID: ${vaultID}.`);
        }
        return beefyVault;
    }
}
exports.BeefyAPI = BeefyAPI;
